import React, { ChangeEvent, useRef } from "react";
import styled from "@emotion/styled";

import { Typography } from "../Typography/Typography";
import { CardFooter } from "../Card/Card";
import { Button } from "../Button";
import * as tokens from "../../design-tokens";

import { ShareSvg } from "../../assets/icons/set/ShareSvg";
import { convertMegabytesToBytes } from "../../utils/utils";

class FileSelectionError extends Error {
  payload: Object;

  constructor(message, payload) {
    super(message);
    this.name = this.constructor.name;
    this.payload = payload;
    Error.captureStackTrace(this, this.constructor);
  }
}

const UPLOAD_SECTION_TEST_IDS = {
  button: "UploadSection-upload-button",
  icon: "UploadSection-upload-button-icon",
  input: "UploadSection-upload-button-hidden-input",
};

const UploadSection = ({
  onError,
  onSelect,
  text,
  accept,
  disabled,
  multiple,
  maxFileSizeInMB,
}: UploadSectionProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleButtonClick = () => {
    fileInputRef?.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files?.length) {
      return onError(new FileSelectionError(`No file selected`, null));
    }

    if (
      maxFileSizeInMB &&
      Array.from(files).some(
        (file) => file.size > convertMegabytesToBytes(maxFileSizeInMB)
      )
    ) {
      const oversizedFiles = Array.from(files).filter(
        (file) => file.size > convertMegabytesToBytes(maxFileSizeInMB)
      );

      return onError(
        new FileSelectionError(
          `File size should not exceed ${maxFileSizeInMB} MB`,
          {
            files: oversizedFiles,
          }
        )
      );
    }

    onSelect(files);
  };

  return (
    <CardFooterStyled
      leftContent={<Typography>{text}</Typography>}
      rightContent={
        <UploadButton
          customTestId={UPLOAD_SECTION_TEST_IDS.button}
          onClick={handleButtonClick}
          disabled={disabled}
          iconRight={
            <ShareSvg
              width={24}
              height={24}
              color={tokens.WHITE}
              style={{ paddingBottom: 2 }}
              data-testid={UPLOAD_SECTION_TEST_IDS.icon}
            />
          }
        >
          Upload
          <input
            type="file"
            accept={accept}
            multiple={multiple}
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            data-testid={UPLOAD_SECTION_TEST_IDS.input}
          />
        </UploadButton>
      }
    />
  );
};

const UploadButton = styled(Button)`
  height: 40px;
  padding: 8px 32px;
`;

const CardFooterStyled = styled(CardFooter)`
  background-color: ${tokens.TINTS_CHARGE_BLUE_80};
`;

interface UploadSectionProps {
  text: string;
  multiple?: boolean;
  disabled?: boolean;
  accept?: string;
  maxFileSizeInMB?: number;
  onSelect: (selectedFiles: FileList) => void;
  onError: (e: FileSelectionError) => void;
}

export type { UploadSectionProps };
export { UploadSection, FileSelectionError, UPLOAD_SECTION_TEST_IDS };
